import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, MagnifyingGlass, House, CheckSquare, Square, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Bulk-assign unassigned property_configs to a landlord. Powers the "Link
 * Properties" button on AdminLandlords. Search by address substring, multi-select,
 * hit Link — sets landlord_id on all selected in one call.
 */
export default function LinkPropertiesModal({ open, landlord, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/property-configs/search`, {
        params: { q, only_unassigned: onlyUnassigned, limit: 100 },
      });
      setResults(data.properties || []);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQ(""); setSelected(new Set()); setResults([]);
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, onlyUnassigned]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(results.map(r => r.id)));
  const clearAll = () => setSelected(new Set());

  const doLink = async () => {
    if (!selected.size) return;
    setLinking(true);
    try {
      const { data } = await api.post(`/landlords/${landlord.id}/link-bulk`, {
        property_config_ids: Array.from(selected),
      });
      toast.success(`Linked ${data.linked} propert${data.linked === 1 ? "y" : "ies"} to ${data.landlord}`);
      onLinked?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
    } finally {
      setLinking(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4" onClick={onClose}
      data-testid="link-props-modal">
      <div className="bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a]">
          <div>
            <div className="font-display font-black uppercase text-lg tracking-tight">Link Properties → {landlord?.name}</div>
            <div className="text-xs text-slate-500 font-bold">Select doors to tag to this landlord.</div>
          </div>
          <button onClick={onClose} data-testid="link-props-close" aria-label="Close"
            className="p-2 hover:bg-slate-100"><X weight="bold" size={16}/></button>
        </div>
        <div className="p-4 border-b border-slate-200 space-y-2">
          <div className="relative">
            <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by address…"
              data-testid="link-props-search"
              className="w-full h-11 pl-9 pr-3 border-2 border-[#0f172a] text-sm font-medium"/>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-600">
            <input type="checkbox" checked={onlyUnassigned} onChange={e => setOnlyUnassigned(e.target.checked)}
              data-testid="link-props-unassigned"
              className="h-4 w-4 accent-[#00C4CC]"/>
            Only show unassigned properties
          </label>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && <div className="p-6 text-center text-xs font-black uppercase tracking-widest text-slate-400 flex items-center justify-center gap-2"><ArrowsClockwise className="animate-spin" size={14}/> Loading…</div>}
          {!loading && results.length === 0 && <div className="p-6 text-center text-sm text-slate-500 italic">No properties match — try a different search or uncheck "only unassigned".</div>}
          {!loading && results.map(p => (
            <button key={p.id} onClick={() => toggle(p.id)}
              data-testid={`link-props-row-${p.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-200 flex items-center gap-3 hover:bg-slate-50 ${selected.has(p.id) ? "bg-amber-50" : ""}`}>
              {selected.has(p.id)
                ? <CheckSquare weight="fill" size={20} className="text-[#00C4CC] flex-shrink-0"/>
                : <Square weight="bold" size={20} className="text-slate-300 flex-shrink-0"/>}
              <House size={14} className="text-slate-400 flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{p.address}{p.unit && <span className="text-slate-500 font-normal"> · Unit {p.unit}</span>}</div>
                {p.landlord_id && <div className="text-[10px] font-black uppercase tracking-widest text-orange-600">Reassign from another landlord</div>}
              </div>
            </button>
          ))}
        </div>
        <div className="p-4 border-t-2 border-[#0f172a] flex items-center gap-2 flex-wrap">
          <div className="text-xs font-black uppercase tracking-widest text-slate-600" data-testid="link-props-selected-count">
            {selected.size} selected
          </div>
          <div className="flex-1"/>
          <button onClick={selectAll} disabled={!results.length} data-testid="link-props-select-all"
            className="h-9 px-3 border-2 border-slate-300 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-40">
            Select All
          </button>
          <button onClick={clearAll} disabled={!selected.size} data-testid="link-props-clear-all"
            className="h-9 px-3 border-2 border-slate-300 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-40">
            Clear
          </button>
          <button onClick={doLink} disabled={!selected.size || linking} data-testid="link-props-submit"
            className="h-10 px-5 bg-[#00C4CC] text-white text-xs font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
            {linking ? "Linking…" : `Link ${selected.size} →`}
          </button>
        </div>
      </div>
    </div>
  );
}
